<template>
  <div
    class="fixed inset-0 z-[100] flex justify-center overflow-y-auto bg-ink/50 px-4 py-6"
  >
    <!-- my-auto (not items-center on the backdrop) so that when the dialog's
         natural height exceeds the viewport (banner + route card + stepper +
         fixed-height body + footer can add up on short mobile screens), the
         backdrop's own overflow-y-auto scrolls the whole dialog into view
         instead of clipping its top/bottom symmetrically the way
         items-center + a non-scrolling parent would. -->
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-md flex flex-col paper-card rounded-md overflow-hidden my-auto">

      <!-- Header -->
      <div class="flex items-start gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <div class="min-w-0 flex-1">
          <p :id="dialogTitleId" class="text-[15px] font-semibold truncate">{{ isEditing ? $t('routeRegistration.form.editTitle') : $t('routeRegistration.form.title') }}</p>
          <p v-if="!isEditing" class="mt-1 text-[11.5px] leading-snug text-ink2">
            {{ $t('routeRegistration.form.whyRegister') }}
          </p>
        </div>
        <button
          @click="$emit('close')"
          class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('liveHome.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Route summary card - shown on every step after the route itself has
           been picked/typed (step 1 already has its own rich UI for that, see
           the "selected route"/textarea blocks below), so there's always
           context for what's being registered. A directory route is shown in
           the same compact stats-row style as the route details panel/sheet
           (name+status, region, distance/elevation/grade icons, time); a
           manual description gets shown as a card too, in full (not
           truncated - unlike a route name, a free-text description can run
           to a few sentences). -->
      <div v-if="step !== 'route' && (selectedRoute || routeDescription)" class="mx-4 mt-3 shrink-0">
        <RouteSummaryCard v-if="selectedRoute && selectedRouteFull" :route="selectedRouteFull" />
        <div
          v-else-if="selectedRoute"
          class="flex items-center gap-2.5 px-3 py-2.5 rounded-md border border-border bg-[hsl(var(--muted)/0.4)]"
        >
          <Route class="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
          <span class="min-w-0 flex-1 text-[12.5px] font-semibold truncate">{{ selectedRoute.name }}</span>
        </div>
        <div v-else class="px-3 py-2.5 rounded-md border border-border bg-[hsl(var(--muted)/0.4)]">
          <p class="text-[12px] leading-snug text-[hsl(var(--foreground)/0.85)] line-clamp-3">{{ routeDescription }}</p>
        </div>
      </div>

      <!-- Stepper -->
      <div class="px-4 pt-3 shrink-0">
        <div class="flex items-center gap-1">
          <div
            v-for="(s, i) in STEPS"
            :key="s.key"
            class="h-1.5 flex-1 rounded-full transition-colors"
            :class="i <= activeStepIndex ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'"
          />
        </div>
        <p class="text-[10.5px] text-ink2 mt-1.5 font-medium">
          {{ $t('routeRegistration.form.stepCounter', { n: activeStepIndex + 1, total: STEPS.length }) }} · {{ STEPS[activeStepIndex].label }}
        </p>
      </div>

      <div v-if="error" class="mx-4 mt-2 flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] shrink-0">
        <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <p class="text-[12px] leading-snug">{{ error }}</p>
      </div>

      <!-- Body - fixed height across every step, so the footer/Далее button never jumps -->
      <div class="overflow-y-auto px-4 py-3.5 space-y-3.5 h-[340px] max-h-[50vh]">

        <!-- Step 1: Маршрут -->
        <section v-if="step === 'route'" class="space-y-3">
          <div class="flex rounded-md border border-border p-0.5 bg-[hsl(var(--muted)/0.3)]">
            <button
              type="button"
              @click="routeMode = 'directory'"
              class="flex-1 h-8 rounded-md text-[12px] font-semibold transition-colors"
              :class="routeMode === 'directory' ? 'bg-sheet shadow-ink-sm text-[hsl(var(--foreground))]' : 'text-ink2'"
            >{{ $t('routeRegistration.form.routeModeDirectory') }}</button>
            <button
              type="button"
              @click="routeMode = 'manual'"
              class="flex-1 h-8 rounded-md text-[12px] font-semibold transition-colors"
              :class="routeMode === 'manual' ? 'bg-sheet shadow-ink-sm text-[hsl(var(--foreground))]' : 'text-ink2'"
            >{{ $t('routeRegistration.form.routeModeManual') }}</button>
            <button
              type="button"
              @click="routeMode = 'track'"
              class="flex-1 h-8 rounded-md text-[12px] font-semibold transition-colors"
              :class="routeMode === 'track' ? 'bg-sheet shadow-ink-sm text-[hsl(var(--foreground))]' : 'text-ink2'"
            >{{ $t('routeRegistration.form.routeModeTrack') }}</button>
          </div>

          <textarea
            v-if="routeMode === 'manual'"
            v-model="routeDescription"
            rows="6"
            :placeholder="$t('routeRegistration.form.routeDescriptionPlaceholder')"
            class="w-full resize-none rounded-md border border-border bg-[hsl(var(--muted)/0.4)] px-3 py-2 text-[13px] leading-snug placeholder:text-ink2 focus:outline-none focus:border-[hsl(var(--primary))]"
          />

          <!-- Свой трек - превью строится целиком на клиенте (разбор GPX
               через DOMParser, полилиния в SVG, дистанция по haversine),
               без похода на сервер: пользователь должен увидеть форму трека
               СРАЗУ при выборе файла и убедиться, что это тот файл - иначе
               риск в случае ЧС искать по вообще левому треку. Настоящая
               обработка (DEM-высоты, точная дистанция) идёт на сервер только
               при отправке формы, см. submit(). -->
          <div v-else-if="routeMode === 'track'" class="space-y-2.5">
            <input ref="trackFileInputRef" type="file" accept=".gpx" class="hidden" @change="onTrackFileChosen" />
            <button
              v-if="!trackFileLabel"
              type="button"
              @click="trackFileInputRef?.click()"
              class="w-full h-24 rounded-md border-2 border-dashed border-[hsl(var(--border))] flex flex-col items-center justify-center gap-1.5 text-ink2 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
            >
              <UploadCloud class="w-5 h-5" />
              <span class="text-[12px] font-semibold">{{ $t('routeRegistration.form.trackUploadPrompt') }}</span>
            </button>
            <div v-else class="rounded-md border border-border bg-[hsl(var(--muted)/0.4)] p-2.5 space-y-2">
              <div class="flex items-center gap-2">
                <FileUp class="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
                <span class="min-w-0 flex-1 text-[12.5px] font-semibold truncate">{{ trackFileLabel }}</span>
                <button type="button" @click="clearTrackFile" class="shrink-0 text-[11px] font-semibold text-[hsl(var(--primary))]">
                  {{ $t('routeRegistration.form.changeRoute') }}
                </button>
              </div>
              <svg v-if="trackPreviewPath" viewBox="0 0 200 120" class="w-full h-24 rounded-md bg-sheet">
                <polyline :points="trackPreviewPath" fill="none" stroke="hsl(var(--primary))" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
              </svg>
              <p v-if="trackClientDistanceKm != null" class="text-[11px] text-ink2">
                {{ $t('routeRegistration.form.trackApproxDistance', { km: trackClientDistanceKm.toFixed(1) }) }}
              </p>
              <p v-if="trackParseError" class="text-[11px] text-[hsl(var(--danger))]">{{ trackParseError }}</p>
            </div>
          </div>

          <template v-else>
            <div
              v-if="selectedRoute"
              class="flex items-center gap-2.5 h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)]"
            >
              <Route class="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
              <span class="min-w-0 flex-1 text-[13px] font-semibold truncate">{{ selectedRoute.name }}</span>
              <button type="button" @click="selectedRoute = null" class="shrink-0 text-[11px] font-semibold text-[hsl(var(--primary))]">
                {{ $t('routeRegistration.form.changeRoute') }}
              </button>
            </div>

            <template v-else>
              <div class="relative rounded-full bg-[hsl(var(--muted)/0.5)] border border-border">
                <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink2 pointer-events-none" />
                <input
                  v-model="routeSearchQuery"
                  type="search"
                  :placeholder="$t('leftPanel.searchPlaceholder')"
                  class="w-full h-9 pl-8 pr-8 bg-transparent rounded-full text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)]"
                />
              </div>
              <!-- Без запроса: вкладки «Избранные / Мои / Все» (избранные - сразу под
                   полем). С запросом: плоский список найденного по всему справочнику. -->
              <div v-if="!routeSearchQuery.trim()" class="flex gap-1.5" role="tablist" :aria-label="$t('routeRegistration.form.routeListLabel')">
                <button
                  v-for="tab in routeTabs"
                  :key="tab.key"
                  type="button"
                  role="tab"
                  :aria-selected="activeRouteTab === tab.key"
                  @click="routeTab = tab.key"
                  class="h-8 px-3 rounded-full border text-[12px] font-medium transition-colors"
                  :class="activeRouteTab === tab.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-accent'"
                >{{ tab.label }}<span class="num ml-1 opacity-70">{{ tab.count }}</span></button>
              </div>
              <div class="space-y-0.5">
                <button
                  v-for="r in filteredRoutes"
                  :key="r.id"
                  type="button"
                  @click="selectRoute(r)"
                  class="w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-[hsl(var(--muted)/0.6)] transition-colors text-left"
                >
                  <Route class="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
                  <span class="min-w-0 flex-1 text-[13px] font-semibold truncate">{{ r.name }}</span>
                </button>
                <p v-if="!routesLoading && filteredRoutes.length === 0" class="py-8 text-center text-sm text-ink2">
                  {{ emptyRoutesText }}
                </p>
                <div v-if="routesLoading" class="py-8 text-center">
                  <Loader2 class="w-5 h-5 animate-spin inline text-ink2" />
                </div>
              </div>
            </template>
          </template>
        </section>

        <!-- Step 2: Даты -->
        <section v-else-if="step === 'dates'" class="space-y-3">
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('routeRegistration.form.startDate') }}</span>
            <input
              v-model="startDate"
              type="date"
              required
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('routeRegistration.form.expectedReturn') }}</span>
            <div class="flex gap-2">
              <input
                v-model="returnDate"
                type="date"
                required
                :min="startDate || undefined"
                class="flex-[3] min-w-0 h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
              />
              <input
                v-model="returnTime"
                type="time"
                required
                class="flex-[2] min-w-0 h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
              />
            </div>
          </label>
        </section>

        <!-- Step 3: Контакты -->
        <section v-else-if="step === 'contacts'" class="space-y-3">
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('routeRegistration.form.contactPhone') }}</span>
            <input
              :value="contactPhone"
              @input="contactPhone = normalizePhoneInput(($event.target as HTMLInputElement).value)"
              type="tel"
              required
              autocomplete="tel"
              placeholder="+7 700 000 00 00"
              class="w-full h-10 px-3 rounded-md border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 transition"
              :class="contactPhoneInvalid
                ? 'border-[hsl(var(--danger))] focus:ring-[hsl(var(--danger)/0.3)] focus:border-[hsl(var(--danger))]'
                : 'border-[hsl(var(--border))] focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)]'"
            />
            <span v-if="contactPhoneInvalid" class="text-[11px] text-[hsl(var(--danger))]">{{ $t('routeRegistration.form.invalidPhone') }}</span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="flex items-center gap-1 text-[11px] font-semibold text-ink2">
              {{ $t('routeRegistration.form.emergencyContactPhone') }}
              <InfoTooltip :text="$t('routeRegistration.form.emergencyContactHint')" />
            </span>
            <input
              :value="emergencyContactPhone"
              @input="emergencyContactPhone = normalizePhoneInput(($event.target as HTMLInputElement).value)"
              type="tel"
              required
              autocomplete="tel"
              placeholder="+7 700 000 00 00"
              class="w-full h-10 px-3 rounded-md border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 transition"
              :class="emergencyContactPhoneInvalid
                ? 'border-[hsl(var(--danger))] focus:ring-[hsl(var(--danger)/0.3)] focus:border-[hsl(var(--danger))]'
                : 'border-[hsl(var(--border))] focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)]'"
            />
            <span v-if="emergencyContactPhoneInvalid" class="text-[11px] text-[hsl(var(--danger))]">{{ $t('routeRegistration.form.invalidPhone') }}</span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('routeRegistration.form.participants') }}</span>
            <textarea
              v-model="participants"
              rows="3"
              :placeholder="$t('routeRegistration.form.participantsPlaceholder')"
              class="w-full resize-none rounded-md border border-border bg-[hsl(var(--muted)/0.4)] px-3 py-2 text-[13px] leading-snug placeholder:text-ink2 focus:outline-none focus:border-[hsl(var(--primary))]"
            />
          </label>
        </section>

        <!-- Step 4: Подтверждение -->
        <section v-else class="space-y-1">
          <div v-if="quick" data-quick-hint class="pb-2 flex flex-col gap-2">
            <p class="text-[13px]">{{ $t('routeRegistration.form.quickHint') }}</p>
            <button type="button" class="btn self-start" @click="step = 'dates'; quick = false">{{ $t('routeRegistration.form.editDetails') }}</button>
          </div>
          <div v-for="row in summaryRows" :key="row.label" class="flex items-start justify-between gap-3 py-2 border-b border-[hsl(var(--border)/0.4)] last:border-0">
            <span class="text-[11.5px] text-ink2">{{ row.label }}</span>
            <span class="text-[12.5px] font-semibold text-right max-w-[60%] truncate">{{ row.value }}</span>
          </div>

          <div v-if="!isEditing" class="pt-3 space-y-1.5">
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                v-model="termsAccepted"
                type="checkbox"
                required
                class="mt-0.5 w-4 h-4 shrink-0 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-[hsl(var(--primary)/0.3)]"
              />
              <span class="text-[12px] leading-snug text-[hsl(var(--foreground)/0.85)]">
                {{ $t('routeRegistration.form.consentCombined') }}
              </span>
            </label>
            <p class="pl-6 text-[11px] text-ink2">
              <button type="button" @click.prevent="showServiceRules = true" class="text-[hsl(var(--primary))] font-semibold underline underline-offset-2">{{ $t('routeRegistration.form.consentServiceRulesLink') }}</button>
              ·
              <button type="button" @click.prevent="showDchsConsent = true" class="text-[hsl(var(--primary))] font-semibold underline underline-offset-2">{{ $t('routeRegistration.form.consentDchsDataLink') }}</button>
            </p>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          type="button"
          @click="goBack"
          class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >
          {{ step === 'route' ? $t('routeRegistration.form.cancel') : $t('routeRegistration.form.back') }}
        </button>
        <button
          v-if="step !== 'confirm'"
          type="button"
          @click="goNext"
          :disabled="!canProceedFromStep"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          {{ $t('routeRegistration.form.next') }} <ChevronRight class="w-3.5 h-3.5" />
        </button>
        <button
          v-else
          type="button"
          @click="submit"
          :disabled="!canSubmit || submitting"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
          {{ isEditing ? $t('routeRegistration.form.saveChanges') : $t('routeRegistration.form.submit') }}
        </button>
      </div>
    </div>

    <LegalDocumentModal v-if="showServiceRules" :document="serviceRules" @close="showServiceRules = false" />
    <LegalDocumentModal v-if="showDchsConsent" :document="dchsDataConsent" @close="showDchsConsent = false" />

    <!-- Success confirmation - shown in place of an immediate close, so the
         user actually sees that the registration went through (and that the
         ДЧС pilot access + "tell someone" advice exists) rather than the
         dialog just vanishing. Dismissing it is what actually closes the
         whole flow (see dismissSuccess). -->
    <div
      v-if="showSuccess"
      ref="successPanel"
      role="dialog"
      aria-modal="true"
      class="fixed inset-0 z-[120] flex items-center justify-center bg-ink/50 px-4"
      @keydown="onDialogKeydown"
    >
      <div class="w-full max-w-xs bg-sheet rounded-xl shadow-ink p-5 text-center">
        <div class="w-11 h-11 mx-auto mb-3 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center">
          <CheckCircle2 class="w-5 h-5" />
        </div>
        <p class="text-[14px] font-semibold mb-1.5">{{ $t('routeRegistration.success.title') }}</p>
        <p class="text-[12.5px] text-ink2 leading-snug mb-4">{{ $t('routeRegistration.success.message') }}</p>
        <button
          type="button"
          @click="dismissSuccess"
          class="w-full h-9 rounded-md bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
        >{{ $t('routeRegistration.terms.close') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import {
  X, ChevronRight, Search, Route, Loader2, CircleAlert, CheckCircle2, UploadCloud, FileUp,
} from "lucide-vue-next";
import api from "../api";
import { useRoutesStore } from "../store/routes";
import { useSavedRoutesStore } from "../store/savedRoutes";
import { useAuthStore } from "../store/auth";
import { serviceRules, dchsDataConsent } from "../content/legalDocuments";
import type { RouteMapItem } from "../types/routes";
import type {
  RouteRegistration,
  RouteRegistrationCreateRequest,
  RouteRegistrationDefaults,
  RouteRegistrationTrack,
  RouteRegistrationUpdateRequest,
} from "../types/routeRegistrations";
import LegalDocumentModal from "./LegalDocumentModal.vue";
import RouteSummaryCard from "./RouteSummaryCard.vue";
import InfoTooltip from "./InfoTooltip.vue";
import { normalizePhoneInput, isValidKzPhone } from "../utils/phone";
import { quickStart } from "../utils/registrationQuickStart";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";

const props = defineProps<{
  presetRoute?: { id: number; name: string; durationMinutes?: number | null } | null;
  // Кнопка "Изменить" на карточке профиля - при заданном значении диалог
  // переключается в режим редактирования: предзаполняется этой регистрацией,
  // отправляет PATCH вместо POST и не показывает повторно чекбоксы согласия
  // (уже зафиксированы при создании).
  editRegistration?: RouteRegistration | null;
}>();

const isEditing = computed(() => !!props.editRegistration);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "registered", registration: RouteRegistration): void;
}>();

const { t } = useI18n();

type Step = "route" | "dates" | "contacts" | "confirm";
const STEP_KEYS: Step[] = ["route", "dates", "contacts", "confirm"];
// Если маршрут уже задан (диалог открыт кнопкой "Регистр." у маршрута) -
// пропускаем шаг 1, выбирать заново нечего. Редактирование тоже начинается
// с шага 1, как и новая регистрация, поскольку любое поле, включая маршрут,
// можно менять.
const step = ref<Step>(props.presetRoute ? "dates" : "route");
// Быстрый путь (utils/registrationQuickStart.ts): даты предзаполнены, сразу подтверждение.
const quick = ref(false);
const activeStepIndex = computed(() => STEP_KEYS.indexOf(step.value));

const STEPS = computed(() => [
  { key: "route" as const, label: t("routeRegistration.form.steps.route") },
  { key: "dates" as const, label: t("routeRegistration.form.steps.dates") },
  { key: "contacts" as const, label: t("routeRegistration.form.steps.contacts") },
  { key: "confirm" as const, label: t("routeRegistration.form.steps.confirm") },
]);

// Разбивает ISO-дату на локальные "YYYY-MM-DD"/"HH:MM" для двух полей ввода
// (обратная операция к expectedReturnAt ниже). Именно локальное время, а
// не UTC, чтобы форма редактирования показывала то же время по стенным
// часам, что выбрал пользователь.
const splitLocalDateTime = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};
const editReturn = props.editRegistration ? splitLocalDateTime(props.editRegistration.expected_return_at) : null;

const contactPhone = ref(props.editRegistration?.contact_phone ?? "");
const emergencyContactPhone = ref(props.editRegistration?.emergency_contact_phone ?? "");
const routeMode = ref<"directory" | "manual" | "track">(
  props.editRegistration?.track_gpx_object_key
    ? "track"
    : props.editRegistration && !props.editRegistration.route_id
      ? "manual"
      : "directory"
);

// Свой трек - файл выбирается на этом шаге, но реально грузится и
// обрабатывается на сервере только в submit() (см. там же), чтобы не
// гонять обработку GPX для визарда, который может быть ещё и отменён.
// uploadedTrack - результат уже состоявшейся обработки: либо от
// предыдущего вызова submit() в этой же сессии диалога (после ошибки на
// более позднем шаге - файл не грузим повторно), либо (в режиме
// редактирования) то, что уже сохранено в самой регистрации.
const trackFileInputRef = ref<HTMLInputElement | null>(null);
const trackFile = ref<File | null>(null);
const uploadedTrack = ref<RouteRegistrationTrack | null>(
  props.editRegistration?.track_gpx_object_key
    ? {
        track_gpx_object_key: props.editRegistration.track_gpx_object_key,
        track_distance_km: props.editRegistration.track_distance_km,
        track_elevation_gain_m: props.editRegistration.track_elevation_gain_m,
        track_elevation_loss_m: props.editRegistration.track_elevation_loss_m,
      }
    : null
);
const trackFileLabel = computed(() => trackFile.value?.name || (uploadedTrack.value ? t("routeRegistration.form.trackExistingFile") : ""));
const trackPreviewPath = ref("");
const trackClientDistanceKm = ref<number | null>(null);
const trackParseError = ref("");
const MAX_TRACK_MB = 20;

const clearTrackFile = () => {
  trackFile.value = null;
  uploadedTrack.value = null;
  trackPreviewPath.value = "";
  trackClientDistanceKm.value = null;
  trackParseError.value = "";
};

// Мгновенное превью БЕЗ похода на сервер: разбор координат прямо в браузере
// (GPX - обычный XML, DOMParser хватает), рисуем ту же форму в SVG и грубо
// прикидываем дистанцию по haversine. Точные дистанция/набор высоты по DEM
// посчитает сервер при отправке - здесь достаточно, чтобы подтвердить
// "это тот файл", не точное число.
const buildTrackClientPreview = async (file: File) => {
  try {
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, "application/xml");
    if (doc.querySelector("parsererror")) throw new Error("parse error");
    const points = Array.from(doc.querySelectorAll("trkpt"))
      .map((el) => ({ lat: parseFloat(el.getAttribute("lat") || ""), lon: parseFloat(el.getAttribute("lon") || "") }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (points.length < 2) throw new Error("no points");

    const lats = points.map((p) => p.lat);
    const lons = points.map((p) => p.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const spanLat = maxLat - minLat || 1e-6;
    const spanLon = maxLon - minLon || 1e-6;
    // viewBox 200x120, отступ 10px по каждому краю; lat инвертирован - в SVG y растёт вниз.
    trackPreviewPath.value = points
      .map((p) => {
        const x = 10 + ((p.lon - minLon) / spanLon) * 180;
        const y = 10 + (1 - (p.lat - minLat) / spanLat) * 100;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");

    const toRad = (v: number) => (v * Math.PI) / 180;
    let distanceM = 0;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lon - a.lon);
      const hav = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
      distanceM += 2 * 6371000 * Math.asin(Math.sqrt(hav));
    }
    trackClientDistanceKm.value = distanceM / 1000;
  } catch {
    trackParseError.value = t("routeRegistration.form.trackParseError");
    trackPreviewPath.value = "";
    trackClientDistanceKm.value = null;
  }
};

const onTrackFileChosen = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  trackParseError.value = "";
  if (!file.name.toLowerCase().endsWith(".gpx")) {
    trackParseError.value = t("routeRegistration.form.trackInvalidFile");
    return;
  }
  if (file.size > MAX_TRACK_MB * 1024 * 1024) {
    trackParseError.value = t("routeRegistration.form.trackTooLarge", { mb: MAX_TRACK_MB });
    return;
  }
  trackFile.value = file;
  // Новый файл - любая ранее сохранённая/загруженная версия трека больше не
  // актуальна, submit() обязан обработать заново.
  uploadedTrack.value = null;
  await buildTrackClientPreview(file);
};
const selectedRoute = ref<{ id: number; name: string } | null>(
  props.editRegistration?.route_id
    ? { id: props.editRegistration.route_id, name: props.editRegistration.route_name ?? "" }
    : (props.presetRoute ?? null)
);
const routeDescription = ref(props.editRegistration?.route_description ?? "");
const participants = ref(props.editRegistration?.participants ?? "");
const startDate = ref(props.editRegistration?.start_date ?? new Date().toISOString().slice(0, 10));
const returnDate = ref(editReturn?.date ?? "");
const returnTime = ref(editReturn?.time ?? "");
// Объединяются для summary/payload в формате "YYYY-MM-DDTHH:MM" - остальному
// коду (форматтер сводки, тело запроса) не важно, что UI хранит дату и время раздельно.
const expectedReturnAt = computed(() => (returnDate.value && returnTime.value ? `${returnDate.value}T${returnTime.value}` : ""));

// У даты выезда нет своего времени (запрашивается только дата), она
// трактуется как 00:00. Поэтому проверка "возврат не раньше выезда"
// сводится к сравнению одних дат, а returnTime своей проверки не требует.
// Сдвигаем returnDate вперёд, если startDate стала позже него - иначе поля
// могут молча остаться в невалидной комбинации, которую min-атрибут не
// отловит (например, при ручном вводе).
watch(startDate, (start) => {
  if (start && returnDate.value && returnDate.value < start) {
    returnDate.value = start;
  }
});
const termsAccepted = ref(false);
const showServiceRules = ref(false);
const showDchsConsent = ref(false);
const submitting = ref(false);
const error = ref("");
const showSuccess = ref(false);
const registeredData = ref<RouteRegistration | null>(null);

const routesStore = useRoutesStore();
const { items: routes, loading: routesLoading } = storeToRefs(routesStore);
const routeSearchQuery = ref("");

const savedRoutes = useSavedRoutesStore();
const auth = useAuthStore();

// Избранные и собственные маршруты видны сразу, без поиска; когда человек
// печатает, вкладки скрываются и ищем по всему справочнику.
type RouteTab = "saved" | "mine" | "all";
const savedList = computed(() => routes.value.filter((r) => savedRoutes.isSaved(r.id)));
const mineList = computed(() => (auth.userId == null ? [] : routes.value.filter((r) => r.owner_user_id === auth.userId)));
const routeTab = ref<RouteTab | null>(null);
// Пока человек вкладку не выбрал: избранные, если они есть, иначе свои, иначе все.
const activeRouteTab = computed<RouteTab>(
  () => routeTab.value ?? (savedList.value.length ? "saved" : mineList.value.length ? "mine" : "all"),
);
const routeTabs = computed(() => {
  const tabs: { key: RouteTab; label: string; count: number }[] = [
    { key: "all", label: t("routeRegistration.form.routeTabAll"), count: routes.value.length },
  ];
  if (auth.isAuthenticated) {
    tabs.push({ key: "saved", label: t("routeRegistration.form.routeTabSaved"), count: savedList.value.length });
    tabs.push({ key: "mine", label: t("routeRegistration.form.routeTabMine"), count: mineList.value.length });
  }
  return tabs;
});

const filteredRoutes = computed(() => {
  const q = routeSearchQuery.value.trim().toLowerCase();
  if (q) return routes.value.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 200);
  const list = activeRouteTab.value === "saved" ? savedList.value : activeRouteTab.value === "mine" ? mineList.value : routes.value;
  return list.slice(0, 200);
});
const emptyRoutesText = computed(() => {
  if (routeSearchQuery.value.trim()) return t("routeRegistration.form.noRoutesFound");
  if (activeRouteTab.value === "saved") return t("routeRegistration.form.noSavedRoutes");
  if (activeRouteTab.value === "mine") return t("routeRegistration.form.noOwnRoutes");
  return t("routeRegistration.form.noRoutesFound");
});

const canProceedRoute = computed(() => {
  if (routeMode.value === "directory") return !!selectedRoute.value;
  if (routeMode.value === "manual") return !!routeDescription.value.trim();
  return !!trackFile.value || !!uploadedTrack.value;
});
const canProceedDates = computed(
  () => !!startDate.value && !!returnDate.value && !!returnTime.value && returnDate.value >= startDate.value
);
// Оба поля телефона обязательны на этом шаге - индикатор "invalid"
// загорается только после того, как что-то введено, чтобы поле не
// стартовало в состоянии ошибки.
const contactPhoneInvalid = computed(() => !!contactPhone.value.trim() && !isValidKzPhone(contactPhone.value));
const emergencyContactPhoneInvalid = computed(() => !!emergencyContactPhone.value.trim() && !isValidKzPhone(emergencyContactPhone.value));
const canProceedContacts = computed(
  () => isValidKzPhone(contactPhone.value) && isValidKzPhone(emergencyContactPhone.value)
);

const canProceedFromStep = computed(() => {
  if (step.value === "route") return canProceedRoute.value;
  if (step.value === "dates") return canProceedDates.value;
  if (step.value === "contacts") return canProceedContacts.value;
  return true;
});

const canSubmit = computed(
  () => canProceedRoute.value && canProceedDates.value && canProceedContacts.value
    && (isEditing.value || termsAccepted.value)
);

const formatDateTimeLocal = (v: string) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleString([], { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const summaryRows = computed(() => [
  { label: t("routeRegistration.form.route"), value: selectedRoute.value?.name || routeDescription.value || trackFileLabel.value || "—" },
  { label: t("routeRegistration.form.startDate"), value: startDate.value || "—" },
  { label: t("routeRegistration.form.expectedReturn"), value: expectedReturnAt.value ? formatDateTimeLocal(expectedReturnAt.value) : "—" },
  { label: t("routeRegistration.form.contactPhone"), value: contactPhone.value || "—" },
  {
    label: t("routeRegistration.form.emergencyContactPhone"),
    value: emergencyContactPhone.value || "—",
  },
  { label: t("routeRegistration.form.participants"), value: participants.value || "—" },
]);

const goNext = () => {
  if (!canProceedFromStep.value) return;
  const i = STEP_KEYS.indexOf(step.value);
  if (i < STEP_KEYS.length - 1) step.value = STEP_KEYS[i + 1];
};

// Первый показываемый шаг: при заданном presetRoute диалог стартует со
// второго ("dates"), назад с него = закрытие, а не переход на "route".
const firstStepIndex = props.presetRoute ? 1 : 0;

// И кнопка "Назад", и системная кнопка браузера идут одним путём:
// history.back() → popstate → колбэк ниже (шаг назад либо закрытие).
const goBack = () => window.history.back();

useHistoryBackClose(
  () => emit("close"),
  () => {
    const i = STEP_KEYS.indexOf(step.value);
    if (i > firstStepIndex) {
      step.value = STEP_KEYS[i - 1];
      return true;
    }
    return false;
  }
);

const selectRoute = (r: RouteMapItem) => {
  selectedRoute.value = { id: r.id, name: r.name };
};

// Полный объект маршрута для карточки сводки (RouteCard нужны
// дистанция/набор высоты/теги/статус, а не только {id, name}) - берётся из
// общего стора маршрутов, а не отдельным запросом, т.к. там уже есть весь
// список.
const selectedRouteFull = computed<RouteMapItem | null>(() => {
  if (!selectedRoute.value) return null;
  return routes.value.find((r) => r.id === selectedRoute.value!.id) ?? null;
});

const loadDefaults = async () => {
  try {
    const res = await api.get<RouteRegistrationDefaults>("/route-registrations/defaults");
    if (res.data?.contact_phone) contactPhone.value = res.data.contact_phone;
    if (res.data?.emergency_contact_phone) emergencyContactPhone.value = res.data.emergency_contact_phone;
    // Открыто с маршрута и телефоны уже сохранены - сразу подтверждение.
    // Только если форму ещё не трогали: медленный ответ не должен затирать
    // введённое пользователем и перескакивать шаги.
    const untouched = step.value === "dates" && !returnDate.value && !returnTime.value;
    const plan = untouched && quickStart({
      hasPresetRoute: !!props.presetRoute,
      isEditing: isEditing.value,
      contactPhone: contactPhone.value,
      emergencyPhone: emergencyContactPhone.value,
      durationMinutes: props.presetRoute?.durationMinutes,
      now: new Date(),
    });
    if (plan) {
      startDate.value = plan.startDate;
      returnDate.value = plan.returnDate;
      returnTime.value = plan.returnTime;
      step.value = "confirm";
      quick.value = true;
    }
  } catch {
    // значений по умолчанию ещё нет (новый пользователь) - форма просто остаётся пустой, это не ошибка
  }
};

onMounted(() => {
  routesStore.fetch();
  if (auth.isAuthenticated && !savedRoutes.loaded) savedRoutes.fetchIds();
  // При редактировании форма уже предзаполнена реальными данными -
  // loadDefaults() тянет значения из истории и затёр бы их.
  if (!isEditing.value) loadDefaults();
});

const submit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    let routeFields: Record<string, unknown>;
    if (routeMode.value === "directory") {
      routeFields = { route_id: selectedRoute.value!.id };
    } else if (routeMode.value === "manual") {
      routeFields = { route_description: routeDescription.value.trim() };
    } else {
      // Реальная загрузка+обработка на сервере - только здесь, при отправке
      // формы, а не при выборе файла на шаге 1 (см. onTrackFileChosen).
      // Если файл уже был обработан раньше в этой же сессии диалога
      // (например, отправка упала на более позднем шаге) - не грузим повторно.
      if (trackFile.value && !uploadedTrack.value) {
        const trackForm = new FormData();
        trackForm.append("gpx_file", trackFile.value);
        const trackRes = await api.post<RouteRegistrationTrack>("/route-registrations/track", trackForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedTrack.value = trackRes.data;
      }
      routeFields = {
        track_gpx_object_key: uploadedTrack.value!.track_gpx_object_key,
        track_geom_wkt: uploadedTrack.value!.track_geom_wkt,
        track_elevation_gain_m: uploadedTrack.value!.track_elevation_gain_m,
        track_elevation_loss_m: uploadedTrack.value!.track_elevation_loss_m,
      };
    }
    if (isEditing.value) {
      const body: RouteRegistrationUpdateRequest = {
        contact_phone: contactPhone.value.trim(),
        emergency_contact_name: null,
        emergency_contact_phone: emergencyContactPhone.value.trim(),
        participants: participants.value.trim(),
        start_date: startDate.value,
        expected_return_at: expectedReturnAt.value,
        ...routeFields,
      };
      const res = await api.patch<RouteRegistration>(
        `/route-registrations/${props.editRegistration!.id}`, body
      );
      // Модалка успеха не показывается при обычном редактировании (она для
      // момента "вы зарегистрированы" при новой регистрации) - закрываем
      // сразу, родитель покажет короткий тост.
      emit("registered", res.data);
      emit("close");
      return;
    }
    const body: RouteRegistrationCreateRequest = {
      contact_phone: contactPhone.value.trim(),
      emergency_contact_name: null,
      emergency_contact_phone: emergencyContactPhone.value.trim(),
      participants: participants.value.trim(),
      start_date: startDate.value,
      expected_return_at: expectedReturnAt.value,
      terms_accepted: termsAccepted.value,
      service_rules_version: serviceRules.version,
      dchs_consent_version: dchsDataConsent.version,
      ...routeFields,
    };
    const res = await api.post<RouteRegistration>("/route-registrations", body);
    // События ещё не эмитятся - "registered"/"close" сработают только когда
    // пользователь закроет модалку успеха ниже, иначе обработчик родителя
    // (который тоже скрывает этот диалог) размонтировал бы модалку до того,
    // как её увидят.
    registeredData.value = res.data;
    showSuccess.value = true;
  } catch (err: any) {
    error.value = err?.response?.status === 409
      ? t("routeRegistration.form.errorAlreadyActive")
      : t("routeRegistration.form.errorGeneric");
  } finally {
    submitting.value = false;
  }
};

const dismissSuccess = () => {
  showSuccess.value = false;
  if (registeredData.value) emit("registered", registeredData.value);
  emit("close");
};

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
// Экран успеха - отдельный оверлей рядом с панелью: пока он открыт, фокус
// и Esc принадлежат ему (Esc закрывает его так же, как кнопка «Понятно»).
const successPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, {
  onClose: () => (showSuccess.value ? dismissSuccess() : emit('close')),
  activeRoot: () => (showSuccess.value ? successPanel.value : null),
});
</script>
